import { ArrowLeft, Utensils, MessageCircle } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const GutHealthScoreDetail = () => {
  const navigate = useNavigate();
  const score = 72;

  const getInsight = (score: number) => {
    if (score >= 80) {
      return {
        emoji: "🌟",
        title: "Outstanding food choices today!",
        message:
          "Your gut is thriving. Your meals are rich in fiber, balanced in macros, and low in processed sugars. Keep this up — your microbiome will thank you.",
        color: "text-primary",
        bg: "bg-primary/10 border-primary/30",
      };
    }
    if (score >= 60) {
      return {
        emoji: "👍",
        title: "Good nutritional balance!",
        message:
          "You're eating well overall. Your meals show a decent mix of nutrients with room to add more fiber or diversify your vegetable intake for an even healthier gut.",
        color: "text-primary",
        bg: "bg-primary/10 border-primary/30",
      };
    }
    if (score >= 40) {
      return {
        emoji: "⚠️",
        title: "Room for improvement",
        message:
          "Your gut health score is moderate. Consider swapping refined carbs for whole grains and adding more fiber-rich foods like legumes, vegetables, or seeds to your meals.",
        color: "text-yellow-600",
        bg: "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700/30",
      };
    }
    return {
      emoji: "🚨",
      title: "Your diet needs attention",
      message:
        "Your gut health score is low. High sugar, low fiber, and poor fat quality are likely contributing. Try starting with one small change — like adding greens to your next meal.",
      color: "text-destructive",
      bg: "bg-destructive/10 border-destructive/30",
    };
  };

  const insight = getInsight(score);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/home")}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-base font-semibold text-foreground">
              Gut Health Score
            </h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Today's Stats */}
        <Card className="rounded-xl border border-border/50 bg-gradient-to-br from-primary/10 to-primary/5 p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Gut Health Score
            </p>
            <div className="mb-2 text-5xl font-bold text-primary">72</div>
            <p className="text-sm text-muted-foreground">of 100 maximum</p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: "72%" }}
              />
            </div>
          </div>
        </Card>

        {/* Insight Message */}
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 ${insight.bg}`}
        >
          <span className="mt-0.5 text-2xl leading-none">{insight.emoji}</span>
          <div>
            <div className={`mb-1 text-sm font-semibold ${insight.color}`}>
              {insight.title}
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {insight.message}
            </p>
          </div>
        </div>

        {/* What is Gut Health Score */}
        <Card className="rounded-xl border border-border/50 bg-gradient-to-br from-primary/5 to-primary/10 p-5 shadow-sm">
          <h3 className="mb-3 flex items-center gap-2 font-bold text-foreground">
            <Utensils className="h-5 w-5 text-primary" />
            What is Gut Health Score?
          </h3>
          <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
            Gut Health Score measures the quality of your dietary intake on a
            scale of 0-100. It evaluates nutrient density, the balance of
            macronutrients, and gut-friendly properties of your meals. A higher
            score indicates better nutritional choices that support digestive
            health and overall wellbeing.
          </p>
          <div className="space-y-2 rounded-lg bg-background/60 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground">
              Calculated From
            </h4>
            <ul className="space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-primary">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Carb-Fiber Balance:
                  </span>{" "}
                  Ratio of carbs to fiber intake
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-primary">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Fat Quality:
                  </span>{" "}
                  Saturated, trans, and total fat levels
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-primary">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Sugar Load:
                  </span>{" "}
                  Sugar intake relative to total carbs
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-primary">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Meal Scores:
                  </span>{" "}
                  Individual meal quality ratings
                </span>
              </li>
            </ul>
          </div>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Score Ranges</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="font-semibold text-primary">80-100</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">60-79</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fair</span>
              <span className="font-semibold">40-59</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">0-39</span>
            </div>
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Ways to Improve</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              <span>Increase variety of colorful vegetables in your meals</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              <span>Choose whole grains over refined carbohydrates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              <span>Include more fermented foods for gut health</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              <span>Prioritize omega-3 rich foods like fish and nuts</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default GutHealthScoreDetail;
